import { useState } from 'react';
import { api, type AcctCorp, type BillForecastAdjustRow, type BillForecastItem, type LiquidityResult } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';

/**
 * 유동성 시재 「입금 예측(청구)」 내역 · 일자조정(#435 · 시트 「통근거래처입금예측시스템」 amendDate 탭).
 * 청구 한 건 = 한 줄. 날짜를 바꾸면 그 날짜가 자동 예측일(청구일 + 거래처 평균 회수일)보다 우선한다(서버 `liquidity.ts`).
 * 연체(예측일 지남) = 입금 안 됐는데 예측일이 오늘 전 — 앞날 입금엔 안 들어가고 여기에만 보인다. 날짜를 다시 주면 그 날 입금에 들어간다.
 * 권한 = 유동성 시재를 볼 수 있는 사람(회계 조회)과 같다.
 */
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function ForecastPanel({ data, corps, onChanged }: { data: LiquidityResult; corps: AcctCorp[]; onChanged: () => void }) {
  const corpName = (id: number) => corps.find((c) => c.corp_id === id)?.short_name ?? String(id);
  const month = Object.values(data.forecast ?? {}).flat().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.cust < b.cust ? -1 : 1));
  const overdue = data.forecastOverdue ?? [];
  const sum = (l: BillForecastItem[]) => l.reduce((s, f) => s + Number(f.amt), 0);
  return (
    <div className="card space-y-3 p-3">
      <Section title={`이 달 입금 예측 ${month.length}건 · ${money(sum(month))}`} rows={month} corpName={corpName} onChanged={onChanged}
        empty="이 달에 들어갈 입금 예측이 없습니다." />
      <Section title={`연체(예측일 지남) ${overdue.length}건 · ${money(sum(overdue))} — 입금에 안 넣음 · 날짜를 다시 주면 그 날 입금에 들어갑니다`}
        rows={overdue} corpName={corpName} onChanged={onChanged} empty="연체된 청구가 없습니다." tone="overdue" />
    </div>
  );
}

function Section({ title, rows, corpName, onChanged, empty, tone }: {
  title: string; rows: BillForecastItem[]; corpName: (id: number) => string; onChanged: () => void; empty: string; tone?: 'overdue';
}) {
  return (
    <div>
      <div className={`mb-1 text-sm font-semibold ${tone === 'overdue' ? 'text-amber-800' : ''}`}>{title}</div>
      {rows.length === 0 ? <p className="text-xs text-ink-faint">{empty}</p> : (
        <div className="max-h-[22rem] overflow-auto rounded border">
          <table className="w-full min-w-[60rem] text-xs [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:bg-canvas">
            <thead>
              <tr className="text-left">
                <th className="px-2 py-1">예상 입금일</th><th className="px-2">거래처</th><th className="px-2">법인</th>
                <th className="px-2">접수·정산월</th><th className="px-2">청구일</th><th className="px-2 text-right">청구액(공급가)</th>
                <th className="px-2">자동 예측일</th><th className="px-2">일자조정</th><th className="px-2">날짜 바꾸기</th>
              </tr>
            </thead>
            <tbody>{rows.map((f) => <Row key={f.misuId} f={f} corpName={corpName} onChanged={onChanged} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ f, corpName, onChanged }: { f: BillForecastItem; corpName: (id: number) => string; onChanged: () => void }) {
  const [date, setDate] = useState(f.adj?.date ?? '');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hist, setHist] = useState<BillForecastAdjustRow[] | null>(null);

  async function save(next: string | null) {
    setBusy(true); setError(null);
    try {
      await api.setBillForecastDate({ misuId: f.misuId, date: next, memo: memo || undefined, autoDate: f.auto });
      setMemo(''); setHist(null);
      onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  async function history() {
    if (hist) { setHist(null); return; }
    try { setHist((await api.listBillForecastAdjust({ misuId: f.misuId })).rows); } catch (e) { setError(errMsg(e)); }
  }

  return (
    <>
      <tr className="border-t align-top">
        <td className="whitespace-nowrap px-2 py-1 font-medium">{f.date}{f.adj && <span className="ml-1 rounded bg-brand-100 px-1 text-[10px]">조정</span>}</td>
        <td className="px-2 py-1">{f.cust}</td>
        <td className="whitespace-nowrap px-2 py-1">{corpName(f.corpId)}</td>
        <td className="whitespace-nowrap px-2 py-1">{f.recvId} · {f.driveMon}</td>
        <td className="whitespace-nowrap px-2 py-1">{f.reqDate}</td>
        <td className="whitespace-nowrap px-2 py-1 text-right">{money(Number(f.amt))}</td>
        <td className="whitespace-nowrap px-2 py-1 text-ink-faint">{f.auto ?? '입금 이력 없음'}</td>
        <td className="px-2 py-1 text-ink-faint">
          {f.adj ? <>{f.adj.by ?? ''} · {f.adj.at ?? ''}{f.adj.memo ? ` · ${f.adj.memo}` : ''}</> : '—'}
          <button className="ml-1 underline" onClick={history}>{hist ? '이력 접기' : '이력'}</button>
        </td>
        <td className="whitespace-nowrap px-2 py-1">
          <input type="date" className="field w-36 py-0.5 text-xs" value={date} onChange={(e) => setDate(e.target.value)} />
          <input className="field ml-1 w-28 py-0.5 text-xs" placeholder="메모(선택)" value={memo} onChange={(e) => setMemo(e.target.value)} />
          <button className="btn-ghost ml-1 px-2 py-0.5 text-xs" disabled={busy || !date || date === f.adj?.date} onClick={() => save(date)}>바꾸기</button>
          {f.adj && <button className="btn-ghost ml-1 px-2 py-0.5 text-xs" disabled={busy} onClick={() => save(null)}>되돌리기</button>}
          {error && <ErrorBox message={error} />}
        </td>
      </tr>
      {hist && (
        <tr className="bg-canvas/40">
          <td colSpan={9} className="px-4 py-1 text-ink-faint">
            {hist.length === 0 ? '조정 이력 없음' : hist.map((h) => (
              <div key={h.adjust_id}>
                {h.created_at} {h.created_by ?? ''} → {h.plan_date}{h.auto_date ? ` (자동 ${h.auto_date})` : ''}{h.memo ? ` · ${h.memo}` : ''}
                {h.is_deleted ? ` · 끝남 ${h.updated_at ?? ''} ${h.updated_by ?? ''}` : ' · 지금 적용'}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}
